import { thinkingLabel, typingStats, type KeystrokeLog } from '@coderaw/log'
import { instrument, type Env } from '@coderaw/runtime'
import { el } from '../../lib/dom'
import { healBrackets } from '../plot/heal'
import { createPlotStage } from '../plot/PlotStage'
import { createConsoleTypeout } from './ConsoleTypeout'
import { createPlayerControls } from './PlayerControls'
import { createPlotFallback } from './plotFallback'
import { createDocCursor, eventsBetween } from './replayState'
import { createTransport, type Transport } from './replayTransport'
import { createTypingStrip } from './TypingStrip'

export type ReplayPlayerOptions = {
  readonly log: KeystrokeLog
  readonly plotId: string
  readonly env: () => Env
  readonly autoplay?: boolean
}

export type ReplayPlayer = { readonly element: HTMLElement; readonly destroy: () => void }

/** A doc is replayable when it compiles once healed; half-typed code never flashes errors. */
const parses = (doc: string): boolean => {
  if (doc.trim() === '') return false
  try {
    instrument(healBrackets(doc))
    return true
  } catch {
    return false
  }
}

const statsLine = (log: KeystrokeLog): string => {
  const s = typingStats(log)
  const corrections = Math.round(s.correctionRatio * 100)
  const assisted = s.assistedChars > 0 ? ` · ${s.assistedChars} assisted` : ''
  const stamps = s.stampChars > 0 ? ` · ${s.stampChars} stamped` : ''
  return `${s.insertedChars} chars typed · ${corrections}% corrected${assisted}${stamps}`
}

type Views = {
  readonly typeout: ReturnType<typeof createConsoleTypeout>
  readonly strip: ReturnType<typeof createTypingStrip>
  readonly controls: ReturnType<typeof createPlayerControls>
  readonly plot: ReturnType<typeof createPlotFallback>
}

/** Pushes the transport's position into the console, plot, strip and clock. */
const createRenderer = (log: KeystrokeLog, transport: Transport, views: Views) => {
  const docCursor = createDocCursor(log)
  const cpCursor = createDocCursor(log)
  let prevN = -1
  let prevCp = -1
  return (): void => {
    const n = transport.opIndex()
    if (n !== prevN) {
      views.typeout.render(docCursor.at(n))
      const events = eventsBetween(log, prevN, n)
      if (events.deleted) views.typeout.flash()
      if (events.pause !== undefined) views.typeout.think(thinkingLabel(events.pause))
      prevN = n
    }
    const cp = transport.checkpoint()
    if (cp !== prevCp) {
      views.plot.show(cp === 0 ? '' : healBrackets(cpCursor.at(cp).doc))
      prevCp = cp
    }
    const schedule = transport.schedule()
    views.strip.setPlayhead(transport.elapsed() * schedule.factor)
    views.controls.setTime(transport.elapsed(), schedule.totalMs)
  }
}

const bindControls = (transport: Transport, render: () => void, syncPlaying: () => void) =>
  createPlayerControls({
    totalMs: () => transport.schedule().totalMs,
    onToggle: () => {
      transport.toggle()
      syncPlaying()
    },
    onRestart: () => {
      transport.restart()
      syncPlaying()
    },
    onScrub: (ms) => {
      transport.scrub(ms)
      render()
    },
    onSpeed: (choice) => {
      transport.setSpeed(choice)
      render()
    },
  })

/** Plays a keystroke log: console typing, plot redrawing at checkpoints, strip, controls. */
export const createReplayPlayer = (options: ReplayPlayerOptions): ReplayPlayer => {
  const { log } = options
  const stage = createPlotStage(options.plotId, options.env, 'replayed plot')
  const transport = createTransport(
    options.autoplay === undefined ? { log, parses } : { log, parses, autoplay: options.autoplay },
  )
  const syncPlaying = (): void => controls.setPlaying(transport.playing())
  const controls = bindControls(
    transport,
    () => render(),
    () => syncPlaying(),
  )
  const views: Views = {
    typeout: createConsoleTypeout(),
    strip: createTypingStrip(log),
    controls,
    plot: createPlotFallback(stage),
  }
  const render = createRenderer(log, transport, views)

  let raf: number | null = null
  const loop = (now: number): void => {
    const wasPlaying = transport.playing()
    if (!transport.tick(now) && wasPlaying) syncPlaying()
    render()
    raf = requestAnimationFrame(loop)
  }
  const onKey = (event: KeyboardEvent): void => {
    if (event.key !== ' ' || event.target instanceof HTMLInputElement) return
    if (event.target instanceof HTMLSelectElement) return
    event.preventDefault()
    transport.toggle()
    syncPlaying()
  }
  const onVisibility = (): void => transport.resync()

  const element = el(
    'section',
    { class: 'replay', 'aria-label': 'replay', tabindex: '-1' },
    el('div', { class: 'replay-row' }, stage.element, views.typeout.element),
    views.strip.element,
    controls.element,
    el('p', { class: 'replay-stats' }, statsLine(log)),
  )
  element.addEventListener('keydown', onKey)
  document.addEventListener('visibilitychange', onVisibility)
  stage.runner.start()
  syncPlaying()
  raf = requestAnimationFrame(loop)

  return {
    element,
    destroy: () => {
      if (raf !== null) cancelAnimationFrame(raf)
      element.removeEventListener('keydown', onKey)
      document.removeEventListener('visibilitychange', onVisibility)
      views.plot.destroy()
      stage.destroy()
    },
  }
}
